import gql from 'graphql-tag'
import enums from '@/utils/enum'
import {
  SET_EDIT_SAVING_STATE,
  UPDATE_USER_EXPERIENCE,
  UPDATE_USER_MARKETS,
  USER_ACTIONS,
} from '@/state/action-types'
import {
  UPDATE_USER_LANGUAGE,
  UPDATE_USER_MARKET,
  UPDATE_USER_MARKET_REGION,
  UPDATE_USER_SAVE_STATUS,
  UPDATE_USER_STATE,
} from '@/state/mutation-types'
import { UPDATE_FREELANCER, UPDATE_USER } from '@/state/apollo/accounts'

export default {
  strict: import.meta.env.VITE_APP_ENV !== 'production',
  state: {
    id: 0,
    hourlyRate: 0,
    monthlyRate: 0,
    dailyRate: 0,
    markets: {},
    skills: [],
    job_ready: '',
    job_ready_date: '',
    job_titles: [],
    industries: [],
    projectsizes: [],
    experienceCategoryTranslationKey: '',
    experience_search_name: '',
    experience: '',
    languages: {
      type: Object,
    },
    saveStatus: 'fresh',
    openCurrencyConversionModal: false,
    automatic_currency_conversion: false,
    has_seen_foreign_currency: false,
  },
  mutations: {
    [UPDATE_USER_STATE](state, payload) {
      state[payload.fieldName] = payload.fieldValue
    },
    [UPDATE_USER_MARKET](state, payload) {
      state.markets[payload.market][payload.fieldName] = payload.fieldValue
    },
    [UPDATE_USER_MARKET_REGION](state, payload) {
      state.markets[payload.market].regions.find(
        (region) => region.id === parseInt(payload.region, 10),
      )[payload.fieldName] = payload.fieldValue // Sets the on site / off site value for a region
    },
    [UPDATE_USER_LANGUAGE](state, { languageKey, level, checked }) {
      state.languages[languageKey].level = checked ? level : ''
    },
    [UPDATE_USER_SAVE_STATUS](state, status) {
      state.saveStatus = status
    },
  },
  actions: {
    // Made to update very specific fields usually outside of the profile page, such as profile settings
    [USER_ACTIONS.UPDATE_USER_FIELD]({ state }, field) {
      return window.apolloProvider.defaultClient.mutate({
        mutation: UPDATE_USER,
        variables: {
          input: { localId: state.id, ...field },
        },
      })
    },

    /**
     * @TODO Merge the usage of the two saving states
     * @TODO The double usage was carried over from use in markets component locally
     */
    [UPDATE_USER_MARKETS]({ commit, dispatch, state }, accountId) {
      dispatch(SET_EDIT_SAVING_STATE, true)
      commit(UPDATE_USER_SAVE_STATUS, 'saving')

      // Converts the states markets into an object that the api works with
      const apiFriendlyMarkets = Object.values(state.markets)
        .filter((a) => a.isSelected && (a.isOnsite || a.isRemote))
        .map(({ marketKey, isOnsite, isRemote, regions }) => ({
          key: marketKey,
          isOnSite: isOnsite,
          isRemote,
          regions: regions
            .filter((a) => a.isSelected && (a.isOnsite || a.isRemote))
            .map((a) => ({ localId: a.id, isOnSite: a.isOnsite, isRemote: a.isRemote })),
        }))

      return new Promise((resolve, reject) => {
        try {
          window.apolloProvider.defaultClient.mutate({
            mutation: UPDATE_FREELANCER,
            variables: {
              input: {
                globalId: accountId,
                markets: apiFriendlyMarkets,
              },
            },
          })
          dispatch(SET_EDIT_SAVING_STATE, false)
          commit(UPDATE_USER_SAVE_STATUS, 'saved')
          setTimeout(() => {
            resolve()
            commit(UPDATE_USER_SAVE_STATUS, 'fresh')
          }, 1000)
        } catch (e) {
          dispatch(SET_EDIT_SAVING_STATE, false)
          commit('sendNotification', {
            type: enums.notification.types.ERROR,
            sender: this,
            severity: enums.notification.severity.CRITICAL,
          })
          reject()
          setTimeout(() => {
            commit(UPDATE_USER_SAVE_STATUS, 'fresh')
          }, 2000)
        }
      })
    },

    /**
     * This will save the user experience level and commit the api results
     * for updated user experience-related props
     * @returns {Promise<void>}
     */
    async [UPDATE_USER_EXPERIENCE]({ commit, dispatch, state }, accountId) {
      dispatch(SET_EDIT_SAVING_STATE, true)
      try {
        const apiRes = await window.apolloProvider.defaultClient.mutate({
          mutation: gql`
            mutation update($input: UpdateFreelancerInput!) {
              updateFreelancer(input: $input) {
                globalId
                name
                experience
                experience_search_name
                experience_value_in_dollars
              }
            }
          `,
          variables: {
            input: {
              globalId: accountId,
              experience: state.experience,
            },
          },
        })
        if (apiRes?.data.updateFreelancer) {
          commit(UPDATE_USER_STATE, {
            fieldName: 'experience_search_name',
            fieldValue: apiRes?.data.updateFreelancer.experience_search_name,
          })
          commit(UPDATE_USER_STATE, {
            fieldName: 'experience_value_in_dollars',
            fieldValue: apiRes?.data.updateFreelancer.experience_value_in_dollars,
          })
        }
        dispatch(SET_EDIT_SAVING_STATE, false)
      } catch (e) {
        commit('sendNotification', {
          type: enums.notification.types.ERROR,
          sender: this,
          severity: enums.notification.severity.CRITICAL,
        })
      }
    },
  },
  getters: {
    userLanguages: (state) => Object.values(state.languages).filter((lang) => lang.level !== ''),
  },
}
